import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { useSearchParams } from '@remix-run/react';

import Breadcrumbs from '~/components/Breadcrumbs';
import BreakpointsContainer from '~/components/BreakpointsContainer';
import Page from '~/components/Layout/Page';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import getCardStyles from '~/pages/index/_utils/getCardStyles';
import ArchiveSection from '~/pages/index/ArchiveSection';
import getHomePage from '~/services/api/home_pages/getHomePage.server';
import getPaginatedPosts from '~/services/api/posts/getPaginatedPosts.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getContentTagIds from '~/utils/getContentTagIds';
import getMergedMetaData from '~/utils/getMergedMetaData';

import type { HomePageLoaderData } from '.';

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const publication = data?.publication;
  const description = data?.description;

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data.requestUrl),
    matches,
    overrides: {
      title: `Archive | ${publication?.name}`,
      description: description,
    },
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': publication?.url,
            name: publication?.name,
          },
        },
      ],
    },
  });
};

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
export const loader: LoaderFunction = async ({ request, params, context }: any) => {
  const { session } = await loaderSession(request);

  const url = new URL(request.url);
  const contentTags = url.searchParams.getAll('content_tags');

  const publication = await context.getPublication(request, session);

  const contentTagIds = getContentTagIds(contentTags, publication?.content_tags);

  const [paginatedPosts, homePage] = await Promise.all([
    getPaginatedPosts(request, session, contentTagIds),
    getHomePage(request, session, {
      status: 'live',
    }),
  ]);

  return json({
    requestUrl: url.href,
    paginatedPosts,
    publication,
    homePage,
    cardStyles: getCardStyles({
      homePage,
      webTheme: publication.web_template.web_theme,
    }),
  });
};

const Archive = () => {
  const { homePage, publication } = useTypedLoaderData<HomePageLoaderData>();
  const [searchParams] = useSearchParams('page');
  const pageNumber = Number.parseInt(searchParams.get('page') || '1', 10); // Convert to a number

  const breadCrumbs = [
    {
      text: publication?.name,
      href: '../',
    },
    {
      text: 'Archive',
      href: undefined,
    },
    {
      text: `Page ${pageNumber}`,
    },
  ];

  return (
    <Page bgColor={homePage.general_section.theme.bg_color} className="pb-8 pt-4">
      <BreakpointsContainer>
        {publication.render_breadcrumbs && (
          <div className="my-4 w-full">
            <Breadcrumbs breadcrumbs={breadCrumbs} homePage={homePage} />
          </div>
        )}

        <ArchiveSection cardOrientation="vertical" isNewWebBuilder={true} />
      </BreakpointsContainer>
    </Page>
  );
};

export default Archive;
